// Matches the artist calls in this theme: the lookup runs a multi-stage
// aggregation, and a cold query that would have answered must not be aborted
// into a "try again" the customer cannot act on.
const COLLECTION_CODES_TIMEOUT_MS = 15000;

async function getOrderCollectionCodes(orderName, email) {
  const controller = new AbortController();
  const timeoutId = setTimeout(
    () => controller.abort(),
    COLLECTION_CODES_TIMEOUT_MS,
  );
  try {
    const response = await fetch(
      `${API_BASE_URL}/public/order-collection-codes`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderName: orderName, email: email }),
        signal: controller.signal,
      },
    );
    if (!response.ok) {
      throw new Error(`Failed to fetch collection codes: ${response.status}`);
    }
    return await response.json();
  } finally {
    clearTimeout(timeoutId);
  }
}

// The label the API answers with for a collection deleted after the purchase.
// Two of those share it, and their codes are not interchangeable.
const DELETED_COLLECTION_LABEL = "your purchased collection";

// One card per collection: an order can carry several codes for the same one,
// and four identical cards read as a bug rather than as four codes. Keyed on
// the name and the cover together, which is as close to an identity as the
// reply carries — two collections sharing both would be the same artwork under
// the same name.
function collectionGroupKey(collection, index) {
  if (!collection.name || collection.name === DELETED_COLLECTION_LABEL) {
    return "ungrouped:" + index;
  }

  return [
    collection.name,
    collection.imageUrl || "",
    collection.delivery || "",
  ].join("|");
}

function groupCollections(collections) {
  const groups = new Map();

  collections.forEach(function (collection, index) {
    const key = collectionGroupKey(collection, index);
    const group = groups.get(key) || {
      name: collection.name || "",
      imageUrl: collection.imageUrl,
      entries: [],
    };
    group.entries.push(collection);
    groups.set(key, group);
  });

  return Array.from(groups.values());
}

function isCodeDelivery(entry) {
  return entry.delivery === "code" || Boolean(entry.code);
}

function isUsableCode(entry) {
  return Boolean(entry.code) && !entry.unusableReason;
}

// What the card says about the whole group, as a state each page words in its
// own copy: one code and four codes do not say the same thing. A reason the
// page does not know yet reads as "unusable" rather than as nothing.
function collectionGroupStatus(group) {
  const codeEntries = group.entries.filter(isCodeDelivery);

  if (!codeEntries.length) {
    const installed = group.entries.every(function (entry) {
      return entry.installed;
    });
    return installed ? "installed" : "installing";
  }

  const usableCount = codeEntries.filter(isUsableCode).length;
  if (usableCount) return usableCount > 1 ? "readyMultiple" : "ready";

  const onlyReason = codeEntries.length === 1 && codeEntries[0].unusableReason;
  return onlyReason === "redeemed" || onlyReason === "expired" ? onlyReason : "unusable";
}
